<script setup lang="ts">
import GbDialog from "@/components/GbDialog.vue";
import OpensInNewTabIcon from "@/components/OpensInNewTabIcon.vue";
import PresentlevelsSkillsLevel from "@/presentlevels/PresentlevelsSkillsLevel.vue";
import PresentlevelsSkillsAccordion from "@/presentlevels/PresentlevelsSkillsAccordion.vue";
import { LevelGroup, LevelOption, SelectedNeedsData, SelectedStrengthData, AlignedGoals } from '@/presentlevels/presentlevels_types.ts'
import { PropType, computed, watch, ref } from 'vue'
import GbButton from "@/components/GbButton.vue";
import { usePresentLevelsStore } from "@/presentlevels/presentlevels.store.ts";
import { getGradeGroups, levelGroupMatchesEnrolledGrade } from "@/presentlevels/skillsGradeUtils.ts";
import ProgressSpinner from 'primevue/progressspinner'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { nanoid } from 'nanoid'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { postAlignedGoalsForSummary } from '@/presentlevels/api_v2.ts'
import { toolkitUrls } from '@/util/links.js'
import { getGoalSubjectLabelFromValue } from '@/contentprofile/no_sel_composable.js'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store.js'

const presentLevelsStore = usePresentLevelsStore()
const visibilityStore = useVisibilityStore()
const contentProfileStore = useContentProfileStore()

const hasSelections = computed<boolean>(() => {
  const hasStrengths = Object.values(presentLevelsStore.selectedSkillsMap.strengths)
  .some(isSelected => isSelected === true)
  const hasNeeds = Object.values(presentLevelsStore.selectedSkillsMap.needs)
  .some(isSelected => isSelected === true)
  return hasStrengths || hasNeeds
})
const submitError = ref<string>('')
const needWarningType = ref<'grade-level' | 'any' | null>(null)

const props = defineProps({
  summaryUuid: {
    type: String,
    required: true
  },
  categoryLabel: {
    type: String,
    default: ''
  },
  categoryUuid: {
    type: String,
    required: true
  },
  subjectLabel: {
    type: String,
    required: true
  },
  skillLevelGroups: {
    type: Array as PropType<Array<LevelGroup>>,
    default: () => []
  },
  disableAutosave: {
    type: Boolean,
    default: false
  },
  statementText: {
    type: String,
    default: ''
  },
  alignedGoals: {
    type: Array as PropType<AlignedGoals>,
    default: () => []
  },
  browseAssessmentDomain: {
    type: String,
    default: ''
  },
  browseAssessmentSubject: {
    type: String,
    default: ''
  },
  browseAssessmentType: {
    type: String,
    default: ''
  },
  isLibrary: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['modal-closed'])

const browseAssessmentsUrl = computed(() => {
  const baseUrl = `${toolkitUrls.browseAssessments}/?ref=presentlevels_skills_modal`
  const params = new URLSearchParams()
  
  if (props.browseAssessmentSubject) {
    params.append('subject', getGoalSubjectLabelFromValue(props.browseAssessmentSubject))
  }
  
  if (props.browseAssessmentDomain && props.browseAssessmentDomain !== '[all categories]') {
    params.append('category', props.browseAssessmentDomain)
  }
  
  if (props.browseAssessmentType && props.browseAssessmentType !== 'All') {
    params.append('type', props.browseAssessmentType)
  }
  
  const queryString = params.toString()
  return queryString ? `${baseUrl}&${queryString}` : baseUrl
})

function handleBrowseAssessmentsClick() {
  analytics.sendEvent(EVENT_NAMES.PRESENTLEVELS.SKILL_SELECTION.BROWSE_ASSESSMENTS, {
    plw_is_library: props.isLibrary,
    plw_session_enrolled_grade: presentLevelsStore.enrolledGradeLabel,
    plw_statement_category: props.categoryLabel,
    plw_statement_subject: props.subjectLabel
  })
}

function closeModal() {
  presentLevelsStore.resetSelectedSkills()
  submitError.value = ''
  emit('modal-closed')
}

// Get all grade groups categorized by their relation to the enrolled grade
const gradeGroups = computed(() => {
  let enrolledGradeUUID = presentLevelsStore.plSession?.enrolled_grade_uuid
  
  if (!enrolledGradeUUID) {
    const summary = presentLevelsStore.subjectSummaries.find((s: { uuid: string }) => s.uuid === props.summaryUuid)
    if (summary) {
      enrolledGradeUUID = summary.grade_uuid
    }
  }
  
  if (!enrolledGradeUUID) {
    return { lowerGrades: [], targetGrades: [], higherGrades: [] }
  }
  
  const enrolledGrade = presentLevelsStore.levelOptions.find((level: { uuid: string }) => level.uuid === enrolledGradeUUID)
  if (!enrolledGrade) {
    return { lowerGrades: [], targetGrades: [], higherGrades: [] }
  }
  return getGradeGroups(props.skillLevelGroups, enrolledGrade.order)
})
const lowerGrades = computed(() => gradeGroups.value.lowerGrades)
const targetGrades = computed(() => gradeGroups.value.targetGrades)
// Ensure higher grade levels are shown in ascending order
const higherGrades = computed(() => {
  const groups = gradeGroups.value.higherGrades || []
  return [...groups].sort((a: LevelGroup, b: LevelGroup) => {
    const aOrder = Math.min(...a.levels.map(level => level.order))
    const bOrder = Math.min(...b.levels.map(level => level.order))
    return aOrder - bOrder
  })
})

function getStrengthsData(): SelectedStrengthData[] {
  const selectedStrengthUUIDs = presentLevelsStore.getSelectedStrengthUUIDs()
  const strengthsData = []
  for (let levelGroup of props.skillLevelGroups) {
    for (let skill of levelGroup.skills) {
      if (selectedStrengthUUIDs.includes(skill.uuid)) {
        // Find the minimum grade order for this level group
        const gradeOrder = Math.min(...levelGroup.levels.map(level => level.order))

        strengthsData.push({
          strength_text: skill.label,
          grade_label: levelGroup.label,
          grade_uuid: levelGroup.levels.map(level => level.uuid).join(','),
          grade_order: gradeOrder
        })
      }
    }
  }
  return strengthsData
}

function getNeedsData(): SelectedNeedsData[] {
  const selectedNeedUUIDs = presentLevelsStore.getSelectedNeedUUIDs()
  const needsData = []
  for (let levelGroup of props.skillLevelGroups) {
    for (let skill of levelGroup.skills) {
      if (selectedNeedUUIDs.includes(skill.uuid)) {
        // Find the minimum grade order for this level group
        const gradeOrder = Math.min(...levelGroup.levels.map(level => level.order))

        needsData.push({
          need_text: skill.label,
          grade_label: levelGroup.label,
          grade_uuid: levelGroup.levels.map(level => level.uuid).join(','),
          grade_order: gradeOrder
        })
      }
    }
  }
  return needsData
}

const levelledStrengths = computed(() => {
  return presentLevelsStore.getLevelledSelectedSkillLabelsFromGroups('strengths', props.skillLevelGroups)
})

const levelledNeeds = computed(() => {
  return presentLevelsStore.getLevelledSelectedSkillLabelsFromGroups('needs', props.skillLevelGroups)
})

const analytics = getAnalyticsTracker().getChildContext({
  plw_statement_category: props.categoryLabel,
  plw_needs: levelledNeeds
})

// Resolve the student's enrolled grade, falling back to the summary's grade.
// Returns null if it can't be determined.
function getEnrolledGrade(): LevelOption | null {
  let enrolledGradeUUID = presentLevelsStore.plSession?.enrolled_grade_uuid
  if (!enrolledGradeUUID) {
    const summary = presentLevelsStore.subjectSummaries.find(s => s.uuid === props.summaryUuid)
    if (summary) {
      enrolledGradeUUID = summary.grade_uuid
    }
  }

  if (!enrolledGradeUUID) return null

  return presentLevelsStore.levelOptions.find(level => level.uuid === enrolledGradeUUID) ?? null
}

function hasEnrolledGradeLevelNeed(): boolean {
  const enrolledGrade = getEnrolledGrade()
  if (!enrolledGrade) return false

  const selectedNeedUUIDs = presentLevelsStore.getSelectedNeedUUIDs()

  // A grade-level need is a selected need whose level group matches the enrolled grade.
  return props.skillLevelGroups.some(levelGroup =>
    levelGroupMatchesEnrolledGrade(levelGroup, enrolledGrade) &&
    levelGroup.skills.some(skill => selectedNeedUUIDs.includes(skill.uuid))
  )
}

// Whether this category actually offers skills at the student's enrolled grade
// level. Some categories cap out below (e.g. Concepts of Print ends at 1st grade)
// or start above the enrolled grade, in which case no grade-level skill exists and
// requiring a grade-level need would be impossible to satisfy.
function hasSkillsAtEnrolledGrade(): boolean {
  const enrolledGrade = getEnrolledGrade()
  if (!enrolledGrade) return false

  return props.skillLevelGroups.some(levelGroup =>
    levelGroup.skills.length > 0 &&
    levelGroupMatchesEnrolledGrade(levelGroup, enrolledGrade)
  )
}

function hasAnyNeeds(): boolean {
  const needsForRequest = getNeedsData()
  return needsForRequest.length > 0
}

function hasEffectStatements(): boolean {
  const effectStatementSubjects = ['Reading', 'Writing', 'Math', 'Alt Academics: Reading', 'Alt Academics: Writing', 'Alt Academics: Math']
  return effectStatementSubjects.includes(props.subjectLabel)
}

function handleSubmitClick() {
  const isAltAcademics = props.subjectLabel.startsWith('Alt Academics:')
  
  // Only check for needs if this subject has effect statements
  if (visibilityStore.effectsVisibility && hasEffectStatements()) {
    // For Alt Academics subjects, check if any needs are selected
    if (isAltAcademics && !hasAnyNeeds()) {
      needWarningType.value = 'any'
      return
    }
    
    // For regular subjects (R, W, M), warn about a missing enrolled-grade-level
    // need only when this category actually offers skills at the enrolled grade.
    // If it caps out below (or starts above) the enrolled grade, a grade-level
    // need can't exist, so skip the warning rather than asking for the impossible.
    if (!isAltAcademics && !hasEnrolledGradeLevelNeed() && hasSkillsAtEnrolledGrade()) {
      needWarningType.value = 'grade-level'
      return
    }
  }
  
  handleSubmit()
}

function proceedWithWarning() {
  needWarningType.value = null
  handleSubmit()
}

function closeNeedWarning() {
  needWarningType.value = null
}

async function handleSubmit() {
  const trace = nanoid()
  const strengths = levelledStrengths.value
  const needs = levelledNeeds.value

  submitError.value = ''

  var needsForRequest = getNeedsData()
  var strengthsForRequest = getStrengthsData()

  try {
    // Check if there's an existing effect statement
    const currentSummary = presentLevelsStore.subjectSummaries.find(s => s.uuid === props.summaryUuid)
    const hasEffectStatement = !!currentSummary?.effect_statement
    
    // Start statement generation request
    const statementPromise = presentLevelsStore.submitStatementSelections({
      summaryUUID: props.summaryUuid,
      categoryLabel: props.categoryLabel,
      categoryUUID: props.categoryUuid,
      strengths: strengthsForRequest,
      needs: needsForRequest,
      plw_trace: trace,
      saveToSummary: !props.disableAutosave,
      statementText: props.statementText,
      alignedGoals: props.alignedGoals || [],
      fetchGoals: false,
      beta: visibilityStore.betaVisibility,
      content_profile: contentProfileStore.profileId,
      ...(visibilityStore.effectsVisibility && hasEffectStatement && { effect_statement_needs_update: true })
    })

    // Start aligned goals request in parallel
    let alignedGoalsPromise: Promise<void> | null = null
    presentLevelsStore.loadingAlignedGoals[props.summaryUuid] = true

    alignedGoalsPromise = (async () => {
      try {
        const alignedGoalsResponse = await postAlignedGoalsForSummary(props.summaryUuid, {
          subject_label: props.subjectLabel,
          grade_label: presentLevelsStore.getGradeLabelForSummary(props.summaryUuid) || '',
          category_label: props.categoryLabel,
          needs: needsForRequest,
          plw_trace: trace
        }, visibilityStore.betaVisibility, contentProfileStore.profileId)

        const goalUUIDs: string[] = []
        for (const categoryData of Object.values(alignedGoalsResponse)) {
          if (categoryData.goals) {
            goalUUIDs.push(...categoryData.goals.map(goal => goal.uuid))
          }
        }
        analytics.sendEvent(EVENT_NAMES.PRESENTLEVELS.ALIGNED_GOALS.FETCHED, {
          plw_trace: trace,
          plw_strengths: strengthsForRequest,
          plw_needs: needsForRequest,
          plw_aligned_goals: goalUUIDs
        })

        await presentLevelsStore.fetchAndUpdateAlignedGoalsForSummary(props.summaryUuid, alignedGoalsResponse)
        await statementPromise
      } catch (error) {
        console.error('Error fetching aligned goals:', error)
        presentLevelsStore.loadingAlignedGoals[props.summaryUuid] = false
      }
    })()

    await statementPromise

    analytics.sendEvent(EVENT_NAMES.PRESENTLEVELS.SKILL_SELECTION.GENERATE, {
      plw_trace: trace,
      plw_strengths: strengths,
      plw_needs: needs
    })
    
    closeModal()
    
  } catch {
    submitError.value = 'Something went wrong. Please try again.'
  }
}

watch(() => props.categoryLabel, () => {
  presentLevelsStore.resetSelectedSkills()
})
</script>

<template>
<GbDialog
  dialog-id="skills-modal"
  :visible="!!props.categoryLabel"
  @update:visible="(visible: boolean) => !visible && closeModal()"
  @hide="closeModal"
  :footerButtons="true"
  :footerClasses="'justify-between border-t-0'"
  :showCloseIcon="false"
  :style="{ width: '718px' }"
  :pt="{ header: { style: 'display: none' }, root: { 'aria-labelledby': 'skills-modal-title' } }"
>
  <template #bodyContent>
    <div class="px-6 pt-2">
      <h2 id="skills-modal-title" class="sr-only">{{ props.categoryLabel }} for {{ props.subjectLabel }}</h2>
      <div class="text-default mb-4">
        <div>
          <div class="text-xl leading-7 text-gray-700 uppercase">{{ props.subjectLabel }}</div>
          <h3 id="skills-modal-category-title" class="text-display-md font-bold" tabindex="-1" autofocus>{{ props.categoryLabel }}</h3>
        </div>
        <span class="text-md">Select at least one skill below as a strength or need, then click Generate Statement.</span>
      </div>
      <form id="skills-form" @submit.prevent="handleSubmitClick" class="flex flex-col">
        <div class="p-3 mb-4 flex items-center justify-between rounded-lg border border-gray-600 bg-gray-200">
          <div>
            <div class="font-medium text-md">Need more baseline data?</div>
            <div class="text-md text-gray-700">Browse relevant assessments for this skill area.</div>
          </div>
          <a 
            :href="browseAssessmentsUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="browse-assessments-link flex text-md items-center gap-2 px-4 py-2 bg-white rounded-md transition-colors whitespace-nowrap"
            @click="handleBrowseAssessmentsClick"
          >
            <OpensInNewTabIcon size="lg" class="flex items-center" />
            Browse Assessments
          </a>
        </div>
        <div class="overflow-auto max-h-[50vh]">
          <div v-for="level in targetGrades" :key="level.label" class="mb-8">
            <PresentlevelsSkillsLevel :level-group="level" />
          </div>

          <PresentlevelsSkillsAccordion
            :level-groups="lowerGrades"
            grade-type="lower"
          />

          <PresentlevelsSkillsAccordion
            :level-groups="higherGrades"
            grade-type="higher"
            :class="{ 'mt-4': lowerGrades.length > 0 }"
          />
        </div>
      </form>
    </div>
  </template>
  
  <template #footerButtonLeft>
    <div class="flex flex-col items-start px-6 pb-3">
      <div v-if="submitError" id="submit-error" class="text-md mb-2 w-72">
        {{ submitError }}
      </div>
      
      <template v-if="presentLevelsStore.generatingStatements[props.summaryUuid]">
        <GbButton 
          id="generate-statement-button"
          type="submit" 
          form="skills-form"
          class="bg-blue-400 text-white w-72" 
          disabled
        >
          <span class="flex items-center justify-center text-display-sm">
            <ProgressSpinner strokeWidth="8" class="stroke-white h-5 w-5" />
            <span class="ml-2">Generating...</span>
          </span>
        </GbButton>
      </template>
      <template v-else>
        <GbButton
          id="generate-statement-button"
          gb-theme="bluePrimary" 
          type="submit"
          form="skills-form" 
          class="w-72"
          :disabled="!hasSelections"
          :tooltipText="!hasSelections ? 'Select at least 1 strength or need to continue.' : ''"
          :tooltipOptions="{ side: 'bottom' }"
          :ariaDescription="!hasSelections ? 'Generate Statement, unavailable. This button is unavailable. Please select at least 1 strength or need to continue.' : ''"
        >
          <span class="flex items-center justify-center text-display-sm">
            <span class="material-icons-round mr-1" aria-hidden="true">add_circle</span>
            Generate Statement
          </span>
        </GbButton>
      </template>
    </div>
  </template>
  
  <template #footerButtonRight>
    <GbButton
      gbTheme="grayTertiary"
      @click="closeModal"
      aria-label="Cancel"
    >
      <span class="text-md">Cancel</span>
    </GbButton>
  </template>
</GbDialog>

<GbDialog
  dialog-id="need-warning"
  :visible="needWarningType !== null"
  @update:visible="(visible: boolean) => { if (!visible) closeNeedWarning() }"
  :showCloseIcon="true"
  :footerButtons="true"
  class="w-[600px]"
  :header="needWarningType === 'grade-level' ? 'No Grade Level Need Selected' : 'No Needs Selected'"
>
  
  <template #bodyContent>
    <div class="px-5 py-5">
      <p class="text-md text-gray-700 leading-5">
        If this is a deficit area, be sure to select at least
        <span class="font-bold">{{ needWarningType === 'grade-level' ? 'one need at the student\'s enrolled grade level' : 'one need' }}</span> in order to develop a compliant statement.
      </p>
    </div>
  </template>
  
  <template #footerButtonLeft>
    <GbButton
      gbTheme="bluePrimary"
      @click="proceedWithWarning"
    >
      <span class="text-md">Generate Statement Anyway</span>
    </GbButton>
  </template>
  
  <template #footerButtonRight>
    <GbButton
      gbTheme="grayTertiary"
      @click="closeNeedWarning"
      aria-label="Go Back"
    >
      <span class="text-md">Go Back</span>
    </GbButton>
  </template>
</GbDialog>
</template>

<style>
.browse-assessments-link {
  border: 1px solid #004D72;
  color: #004D72;
}

.browse-assessments-link:hover {
  background: #E6E6E6 !important;
  border: 1px solid #6F6F6F;
}

.browse-assessments-link:focus {
  outline: 2px solid #6F6F6F;
  border: 1px solid #6F6F6F;
  outline-offset: 2px;
  background: #E6E6E6 !important;
}
</style>